/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { Box, Text } from "@nordcloud/gnui";
import { Breadcrumbs, showSuccess } from "~/components";
import { availableRoles } from "~/components/Employees/EmployeesRoleSidebar";
import { useShowError } from "~/components/Error/useShowError";
import { InlineEditDropdown } from "~/components/InlineEdit/InlineEditDropdown";
import { DetailRow } from "~/components/Utils/DetailRow";
import { navItems } from "~/constants";
import { useUserConsumer } from "~/context/User/User";
import { Employee } from "~/models";
import { ACTIONS, Can, isStandardUser, ROUTES } from "~/routing";
import { useAxios, useDetailsNotFound } from "~/tools";
import { getEmployeeStatusColor } from "~/tools/display/displayEmployeeColor";
import { EmployeeRoles } from "~/views/employees/EmployeeDetails/EmployeeRoles";

type RouteParams = {
  employeeId: string;
};

function EmployeeDetails() {
  const state = useParams<RouteParams>();
  const { employeeId } = state;
  const [employee, setEmployee] = React.useState<Employee>({
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    permission: "",
    status: "active",
    customers: [],
  });

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { detailsNotFound } = useDetailsNotFound();

  const fetchEmployee = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/employees/${employeeId}`);
      setEmployee(data);
    } catch (e) {
      detailsNotFound(ROUTES.employees.index, navItems.employees);
    }
  }, [Axios, detailsNotFound, employeeId]);

  React.useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const updatePermission = async (permission: string) => {
    try {
      await Axios().put(`/employees/${employeeId}/permission`, { permission });
      showSuccess("Changes will be reflected in the system after 5 minutes");
      await fetchEmployee();
    } catch (e) {
      showError(e.response);
    }
  };

  const fullName = `${employee?.firstName} ${employee?.lastName}`;

  const breadcrumbLabels = employee
    ? [{ value: employee?.id, label: fullName }]
    : undefined;

  return (
    <Container>
      <Breadcrumbs title={fullName} labels={breadcrumbLabels} />

      <Box spacing="spacing04">
        <Text weight="medium">Employee Details</Text>
        <DetailRow title="First name" minWidth="7rem">
          {employee?.firstName}
        </DetailRow>

        <DetailRow title="Last name" minWidth="7rem">
          {employee?.lastName}
        </DetailRow>

        <DetailRow title="Email" minWidth="7rem">
          {employee?.email}
        </DetailRow>

        <DetailRow title="Active" minWidth="7rem">
          <span style={{ color: getEmployeeStatusColor(employee) }}>
            {employee.status === "deactivated" ? "No" : "Yes"}
          </span>
        </DetailRow>

        <Can action={ACTIONS.EDIT_EMPLOYEE}>
          <DetailRow title="Role" minWidth="7rem">
            <InlineEditDropdown
              name="permission"
              options={availableRoles}
              defaultValue={employee.permission || ""}
              onSubmit={updatePermission}
            />
          </DetailRow>
        </Can>
      </Box>

      <EmployeeRoles
        employee={employee}
        canEdit={canEdit}
        refetch={fetchEmployee}
      />
    </Container>
  );
}

export { EmployeeDetails };
